import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  bookingsQuery,
  tabToFilter,
  BOOKING_TABS,
  type BookingSummary,
  type BookingTab,
} from '@/lib/bookings';
import { apartmentsQuery, type Apartment } from '@/lib/apartments';
import { usePermissions } from '@/lib/me';
import { usePageHeader } from '@/components/PageHeader';
import { AlertTriangle, CalendarX2, Plus } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { formatMoney, formatDate } from '@/lib/format';
import { daysBetweenYMD } from '@/lib/calendar';
import { Button } from '@/components/ui/button';
import { Tabs } from '@/components/ui/tabs';
import {
  BookingStatusBadge,
  BookingSourceBadge,
  OperationalStatusBadge,
} from '@/components/StatusBadges';
import { BookingDrawer } from '@/components/BookingDrawer';
import { Dialog } from '@/components/ui/dialog';
import { NewBookingForm } from '@/components/booking/NewBookingForm';
import { MultiSelectFilter } from '@/components/ui/multi-select-filter';
import { DateRangePicker } from '@/components/ui/date-picker';

// Channel options for the source filter (Expedia omitted — not in scope, CLAUDE.md).
const CHANNEL_OPTIONS = [
  { value: 'direct', label: 'Direct' },
  { value: 'booking_com', label: 'Booking.com' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'manual', label: 'Manual' },
];

export const Route = createFileRoute('/_app/bookings/')({
  // Pre-fetch apartments so the apartment column doesn't flash UUIDs while the
  // bookings query is resolving.
  loader: ({ context }) => context.queryClient.ensureQueryData(apartmentsQuery),
  component: BookingsPage,
});

const LIMIT = 50;

export function BookingsPage() {
  const [tab, setTab] = useState<BookingTab>('all');
  const [selectedApartments, setSelectedApartments] = useState<string[]>([]);
  const [checkinFrom, setCheckinFrom] = useState('');
  const [checkinTo, setCheckinTo] = useState('');
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  // Row click opens a quick-view drawer; the Ref link still deep-links the page.
  const [openId, setOpenId] = useState<string | null>(null);
  const [newBookingOpen, setNewBookingOpen] = useState(false);

  const { status: tabStatus } = tabToFilter(tab);

  const filter = {
    status: tabStatus,
    apartmentIds: selectedApartments,
    checkinFrom: checkinFrom || undefined,
    checkinTo: checkinTo || undefined,
    source: selectedSources,
    limit: LIMIT,
    offset: page * LIMIT,
  };

  const { data, isLoading, error } = useQuery(bookingsQuery(filter));
  const { data: apartments } = useQuery(apartmentsQuery);
  // Gate the manual-booking affordance on the effective permission (roles +
  // direct grants) — the same action the backend POST /bookings authorizes.
  const { can } = usePermissions();
  const userCanWrite = can('bookings.write');

  // Title lives in the Topbar (navTitle); publish the live count as the subtitle
  // and the New booking action (bookings.write roles only) into the Topbar actions slot.
  usePageHeader({
    subtitle: data
      ? `${data.total} booking${data.total === 1 ? '' : 's'}`
      : undefined,
    actions: userCanWrite ? (
      <Button variant="primary" size="sm" onClick={() => setNewBookingOpen(true)}>
        <Plus className="mr-1 h-4 w-4" aria-hidden="true" /> New booking
      </Button>
    ) : undefined,
  });

  const aptMap = new Map<string, Apartment>(
    (apartments ?? []).map((a) => [a.id, a]),
  );

  const is401 = error instanceof ApiError && error.status === 401;
  const errorMessage = is401
    ? 'Your session has expired.'
    : error instanceof ApiError
      ? error.message
      : error
        ? String(error)
        : null;

  const total = data?.total ?? 0;
  const itemsLen = data?.items.length ?? 0;
  const from = total === 0 || itemsLen === 0 ? 0 : page * LIMIT + 1;
  const to = page * LIMIT + itemsLen;
  const hasPrev = page > 0;
  const hasNext = to < total;

  function changeTab(next: string) {
    setTab(next as BookingTab);
    setPage(0);
  }

  function clearFilters() {
    setSelectedApartments([]);
    setCheckinFrom('');
    setCheckinTo('');
    setSelectedSources([]);
    setPage(0);
  }

  const hasActiveFilters =
    selectedApartments.length > 0 ||
    checkinFrom !== '' ||
    checkinTo !== '' ||
    selectedSources.length > 0;

  return (
    <>
      {/* Status tabs */}
      <Tabs
        variant="pill"
        value={tab}
        onChange={changeTab}
        tabs={BOOKING_TABS}
        className="mb-6 w-fit"
      />

      {/* Toolbar */}
      <div className="mb-6 flex flex-wrap items-end gap-3">
        {/* Apartment multi-select */}
        <MultiSelectFilter
          label="Apartments"
          options={(apartments ?? []).map((a) => ({ value: a.id, label: a.code }))}
          selected={new Set(selectedApartments)}
          onChange={(next) => { setSelectedApartments([...next]); setPage(0); }}
          searchPlaceholder="Search apartments…"
        />

        {/* Date range picker */}
        <DateRangePicker
          value={{ from: checkinFrom || undefined, to: checkinTo || undefined }}
          onChange={(r) => { setCheckinFrom(r.from ?? ''); setCheckinTo(r.to ?? ''); setPage(0); }}
          placeholder="Check-in dates"
        />

        {/* Source multi-select */}
        <MultiSelectFilter
          label="Channels"
          options={CHANNEL_OPTIONS}
          selected={new Set(selectedSources)}
          onChange={(next) => { setSelectedSources([...next]); setPage(0); }}
          searchable={false}
        />

        {/* Clear filters */}
        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters} className="self-end">
            Clear filters
          </Button>
        )}
      </div>

      {/* Loading */}
      {isLoading && <p className="text-sm text-muted">Loading…</p>}

      {/* Error */}
      {errorMessage && (
        <div
          role="alert"
          className="mb-6 flex items-start gap-3 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" aria-hidden="true" />
          <span>
            {errorMessage}
            {is401 && (
              <>
                {' '}
                <Link to="/login" className="font-semibold underline">
                  Sign in again
                </Link>
                .
              </>
            )}
          </span>
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !errorMessage && data && data.items.length === 0 && (
        <div className="rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card px-5 py-12 text-center">
          <CalendarX2 className="mx-auto mb-3 h-8 w-8 text-muted" aria-hidden="true" />
          <p className="text-sm text-muted">
            {hasActiveFilters
              ? 'No bookings match the current filters.'
              : 'No bookings in this view.'}
          </p>
        </div>
      )}

      {/* Table */}
      {data && data.items.length > 0 && (
        <section>
          <p className="mb-3 text-sm text-muted">
            Showing {from}–{to} of {total} booking{total !== 1 ? 's' : ''}
          </p>
          <BookingsTable items={data.items} aptMap={aptMap} onOpen={setOpenId} />

          {/* Pagination */}
          <div className="mt-4 flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => p - 1)}
              disabled={!hasPrev}
            >
              Previous
            </Button>
            <span className="text-sm text-muted">
              Page {page + 1} of {Math.max(1, Math.ceil(total / LIMIT))}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasNext}
            >
              Next
            </Button>
          </div>
        </section>
      )}

      <BookingDrawer bookingId={openId} onClose={() => setOpenId(null)} />

      <Dialog
        open={newBookingOpen}
        onClose={() => setNewBookingOpen(false)}
        title="New booking"
        description="Create an offline / walk-in booking for an apartment."
        side
      >
        {newBookingOpen && (
          <NewBookingForm onClose={() => setNewBookingOpen(false)} />
        )}
      </Dialog>
    </>
  );
}

/** Stay date range, e.g. "Thu, Aug 13, 2026 → Sat, Aug 15, 2026". */
function formatDateRange(a: string, b: string): string {
  return `${formatDate(a)} → ${formatDate(b)}`;
}

export function BookingsTable({
  items,
  aptMap,
  onOpen,
}: {
  items: BookingSummary[];
  aptMap: Map<string, Apartment>;
  /** Open the quick-view drawer for a booking (row click / Enter / Space). */
  onOpen?: (id: string) => void;
}) {
  return (
    <div className="overflow-x-auto overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-sunken)] text-left text-xs font-semibold uppercase tracking-[0.06em] text-muted">
            <th scope="col" className="px-4 py-3">ID</th>
            <th scope="col" className="px-4 py-3">Guest</th>
            <th scope="col" className="px-4 py-3">Apartment</th>
            <th scope="col" className="px-4 py-3">Source</th>
            <th scope="col" className="px-4 py-3">Dates</th>
            <th scope="col" className="px-4 py-3">Status</th>
            <th scope="col" className="px-4 py-3 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {items.map((booking) => {
            const apt = aptMap.get(booking.apartment_id);
            const aptLabel = apt ? apt.code : booking.apartment_id;
            const shortId = booking.id.slice(0, 8);
            return (
              <tr
                key={booking.id}
                onClick={onOpen ? () => onOpen(booking.id) : undefined}
                onKeyDown={
                  onOpen
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onOpen(booking.id);
                        }
                      }
                    : undefined
                }
                role={onOpen ? 'button' : undefined}
                tabIndex={onOpen ? 0 : undefined}
                aria-label={onOpen ? `Open booking ${booking.guest_name}` : undefined}
                className={`border-b border-[var(--border-soft)] transition last:border-b-0 hover:bg-[var(--surface-sunken)] ${
                  onOpen ? 'cursor-pointer' : ''
                }`}
              >
                <td className="px-4 py-3">
                  <Link
                    to="/bookings/$id"
                    params={{ id: booking.id }}
                    onClick={(e) => e.stopPropagation()}
                    className="font-mono text-xs text-primary hover:underline"
                    aria-label={`Open booking ${booking.id}`}
                    title={booking.id}
                  >
                    {shortId}…
                  </Link>
                </td>
                <td className="px-4 py-3 font-semibold text-[var(--text-heading)]">
                  {booking.guest_name}
                </td>
                <td className="px-4 py-3">
                  <span
                    className="font-mono text-xs text-muted"
                    title={apt ? apt.title : booking.apartment_id}
                  >
                    {aptLabel}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <BookingSourceBadge source={booking.source} />
                </td>
                <td className="px-4 py-3">
                  <div className="tabular-nums">
                    {formatDateRange(booking.checkin_date, booking.checkout_date)}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                    <span>
                      {daysBetweenYMD(booking.checkin_date, booking.checkout_date)} nights · {booking.adults}·{booking.children}·{booking.infants}
                    </span>
                    <OperationalStatusBadge status={booking.operational_status} />
                  </div>
                </td>
                <td className="px-4 py-3">
                  <BookingStatusBadge status={booking.status} />
                </td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {formatMoney(booking.total_amount_minor, booking.currency)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
